import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { NodeSelection, Transaction } from "@tiptap/pm/state";
import { EditorContent, useEditor } from "@tiptap/react";
import { type ComponentProps, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { joinStaticContentUrl } from "utils/staticContent/staticContent";

import { sanitizeEditableContentBlockHtmlForAdminSave } from "./contentBlockHtml";
import {
  normaliseContentBlockImgKey,
  rewriteContentBlockHtmlForEditor,
  rewriteContentBlockHtmlForStorage,
} from "./contentBlockStaticAssets";
import {
  editableContentBlockEditorExtensions,
  EditableContentBlockMenuBar,
  type EditableContentBlockNotification,
} from "./EditableContentBlockEditorShell";
import { exitEditableContentEditor } from "./editableContentBlockWindow";
import awsImageFunctions from "../../helpers/awsImageFunctions";
import {
  useGetContentQuery,
  useUpdateEditableContentBlockMutation,
} from "../../redux/editableContentBlock";
import { getErrorMessage } from "../../utils/errorHandling";
import {
  EDITABLE_CONTENT_BLOCK_DEFAULT_EDITOR_HTML,
  EDITABLE_CONTENT_BLOCK_EDITOR_TIPS,
  EDITABLE_CONTENT_BLOCK_ROUTE_SUBTITLE,
  EDITABLE_CONTENT_BLOCK_ROUTE_TITLE,
} from "../../vnext/routes/settings/content-block/editableContentBlockRouteModel";
import { SettingsWorkspaceChrome } from "../../vnext/routes/settings/SettingsWorkspaceChrome";

const STATIC_CONTENT_IMG_BASE_URL = joinStaticContentUrl("img/");

// Keep the WYSIWYG editing surface aligned with how content blocks render in the customer app.
// See: docs/guides/HOVER_HELP.md (Content blocks).
/* eslint-disable no-inline-colors/no-inline-colors -- This editor surface intentionally owns the fixed content-block styling used to mirror customer-facing rendering. */
const APP_MODAL_BACKGROUND_COLOR = "#FFFCF5";

const CONTENT_BLOCK_WYSIWYG_SX = {
  "& .ProseMirror": {
    outline: "none",
    overflowWrap: "anywhere",
    "& h2": {
      fontWeight: 600,
      color: "#0C0140",
      marginTop: 0,
      marginBottom: 16,
    },
    "& h3": {
      fontWeight: 600,
      color: "black",
      marginTop: 16,
      marginBottom: 8,
    },
    "& h4, & h5": {
      fontWeight: 600,
      marginTop: 16,
      marginBottom: 8,
    },
    "& p": {
      marginTop: 8,
      marginBottom: 8,
    },
    "& ul, & ol": {
      paddingInlineStart: "1.25em",
      marginTop: 8,
      marginBottom: 8,
    },
    "& li": {
      marginTop: 4,
      marginBottom: 4,
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
    },
    "& hr": {
      border: "none",
      borderTop: "1px solid rgba(0,0,0,0.12)",
      marginTop: 16,
      marginBottom: 16,
    },
    "& blockquote": {
      marginTop: 8,
      marginBottom: 8,
      paddingLeft: 16,
      borderLeft: "4px solid rgba(0,0,0,0.12)",
      color: "rgba(0,0,0,0.6)",
    },
    "& > :first-of-type": { marginTop: 0 },
    "& > :last-of-type": { marginBottom: 0 },
  },
} as const;
/* eslint-enable no-inline-colors/no-inline-colors */

const getEditableContentBlockHtml = (
  contentRecord: { content: string; title?: string } | undefined,
) => contentRecord?.content ?? EDITABLE_CONTENT_BLOCK_DEFAULT_EDITOR_HTML;

type EditableContentEditor = NonNullable<
  ComponentProps<typeof EditorContent>["editor"]
>;

const EditableContentBlock = () => {
  const { contentId } = useParams<{ contentId: string }>();
  const [linkText, setLinkText] = useState<string>();
  const [selectedNode, setSelectedNode] = useState<ProseMirrorNode | null>(
    null,
  );
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: "success" | "error";
    message: string;
  }>({ open: false, severity: "success", message: "" });

  const notify = useCallback(
    (notification: EditableContentBlockNotification) => {
      setSnackbar({ open: true, ...notification });
    },
    [],
  );

  if (!contentId) throw new Error("Missing content id");

  const contentResult = useGetContentQuery(contentId, { skip: !contentId });
  const [updateContent] = useUpdateEditableContentBlockMutation();

  const editor = useEditor({
    extensions: editableContentBlockEditorExtensions,
    editorProps: {
      attributes: {
        class: "editor",
      },
    },
    onUpdate: ({ transaction }: { transaction: Transaction }) => {
      // Compare images in the content before and after the transaction to keep our bucket clean.
      const imgSrcs = new Set<string>();
      transaction.doc.forEach((node: ProseMirrorNode) => {
        const src = node.attrs["src"];
        if (
          typeof src === "string" &&
          src.startsWith(STATIC_CONTENT_IMG_BASE_URL)
        ) {
          imgSrcs.add(src);
        }
      });

      transaction.before.forEach((node: ProseMirrorNode) => {
        const src = node.attrs["src"];
        if (
          typeof src === "string" &&
          src.startsWith(STATIC_CONTENT_IMG_BASE_URL) &&
          !imgSrcs.has(src)
        ) {
          const key = normaliseContentBlockImgKey(src);
          if (key) {
            void awsImageFunctions
              .deleteFileFromAws(key)
              .catch(() => undefined);
          }
        }
      });
    },
    onSelectionUpdate: ({ editor }: { editor: EditableContentEditor }) => {
      if ("node" in editor.view.state.selection) {
        const node = (editor.view.state.selection as NodeSelection).node;
        setSelectedNode(node);
      } else {
        setSelectedNode(null);
      }
    },
  });

  useEffect(() => {
    if (contentResult.isSuccess && editor.isEmpty) {
      const contentForEditor = getEditableContentBlockHtml(contentResult.data);

      const contentWithBaseUrl =
        rewriteContentBlockHtmlForEditor(contentForEditor);

      editor.commands.setContent(contentWithBaseUrl);
    }
  }, [editor, contentResult]);

  const contentTitle = contentResult.data?.title ?? "";

  const handleSaveChanges = async (): Promise<void> => {
    const rawHtml = editor.getHTML();
    const effectiveLinkText = linkText ?? contentTitle;
    let cleanHtml = rawHtml.replace(
      /<span data-formula="([^"]+)" data-number-format="([^"]+)"( data-abs="([^"]+)")?[^>]*>[^<]*<\/span>/g,
      '<span data-formula="$1" data-number-format="$2" data-abs="$4"></span>',
    );

    const storageReadyHtml = rewriteContentBlockHtmlForStorage(cleanHtml);
    cleanHtml = sanitizeEditableContentBlockHtmlForAdminSave(storageReadyHtml);
    const updateData = {
      contentId,
      content: cleanHtml,
      ...(effectiveLinkText && { title: effectiveLinkText }),
    };

    try {
      await updateContent(updateData).unwrap();
      exitEditableContentEditor();
    } catch (error) {
      notify({
        severity: "error",
        message: `There was a problem saving your changes. ${getErrorMessage(error)}`,
      });
    }
  };

  const handleDiscardChanges = () => {
    setDiscardConfirmOpen(true);
  };

  const confirmDiscardChanges = async () => {
    setIsDiscarding(true);

    // Remove any uploaded images that weren't in the original HTML snapshot.
    const originalContent = contentResult.data?.content ?? "";
    const imgTags = editor.getHTML().match(/<img[^>]+>/g);
    if (imgTags) {
      await Promise.all(
        imgTags.map(async (imgTag: string) => {
          const src = imgTag.match(/src="([^"]+)"/)?.[1];
          if (!src) return;

          const fileName = src.split("/").pop();
          if (!fileName) return;

          if (!originalContent.includes(fileName)) {
            await awsImageFunctions.deleteFileFromAws(fileName);
          }
        }),
      );
    }

    setIsDiscarding(false);
    setDiscardConfirmOpen(false);
    exitEditableContentEditor();
  };

  return (
    <SettingsWorkspaceChrome
      subtitle={EDITABLE_CONTENT_BLOCK_ROUTE_SUBTITLE}
      title={EDITABLE_CONTENT_BLOCK_ROUTE_TITLE}
    >
      <Container maxWidth="lg">
        <Typography variant="h5">Editing block: {contentId}</Typography>
        <Stack
          direction="row"
          spacing={2}
          sx={{ padding: "10px 0", alignItems: "center" }}
        >
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Link text:
          </Typography>
          <TextField
            size="small"
            placeholder="Tell me what this means"
            value={linkText ?? contentTitle}
            onChange={(e) => setLinkText(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
        </Stack>
        <Alert severity="info">
          This editor changes help content shown in the customer app. Open the
          matching guide first if you need the operator workflow, then use these
          editing tips before you save:
          <ul>
            {EDITABLE_CONTENT_BLOCK_EDITOR_TIPS.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </Alert>
        <Stack direction="column" spacing={2}>
          <Divider orientation="horizontal" />
          <EditableContentBlockMenuBar
            editor={editor}
            filePrefix={contentId}
            selectedNode={selectedNode}
            onNotify={notify}
          />
          <Divider orientation="horizontal" />
          <Paper
            variant="outlined"
            sx={{
              backgroundColor: APP_MODAL_BACKGROUND_COLOR,
              padding: 3,
              borderRadius: 2,
            }}
          >
            <Box sx={CONTENT_BLOCK_WYSIWYG_SX}>
              <EditorContent editor={editor} />
            </Box>
          </Paper>
          <Divider orientation="horizontal" />
        </Stack>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <IconButton onClick={() => void handleSaveChanges()} color="success">
            <SaveIcon />
            Save and close
          </IconButton>
          <IconButton onClick={handleDiscardChanges} color="error">
            <DeleteForeverIcon />
            Abandon changes
          </IconButton>
        </Stack>
        <Dialog
          open={discardConfirmOpen}
          onClose={() => {
            if (isDiscarding) return;
            setDiscardConfirmOpen(false);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Discard changes?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Close the editor and discard your changes? Any images uploaded in
              this session that are not part of the saved content will be
              deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDiscardConfirmOpen(false)}
              disabled={isDiscarding}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={isDiscarding}
              onClick={() => void confirmDiscardChanges()}
            >
              {isDiscarding ? "Discarding..." : "Discard"}
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </SettingsWorkspaceChrome>
  );
};

export default EditableContentBlock;
